import { useCallback, useEffect, useState } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';

const BLANK = { question: '', options: ['', '', '', ''], correct_index: 0 };

function QuestionDialog({ value, onClose, onSaved }) {
  const [q, setQ] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (value) setQ({ question: value.question || '', options: [...(value.options || []), '', '', '', ''].slice(0, 4), correct_index: value.correct_index ?? 0 }); }, [value]);
  const save = async () => {
    setSaving(true);
    try {
      if (value.id) await api.put(`/sales/exam/questions/${value.id}`, q); else await api.post('/sales/exam/questions', q);
      toast.success('Question saved'); onSaved(); onClose();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!value} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="question-dialog">
        <DialogHeader><DialogTitle className="font-heading">{value?.id ? 'Edit Question' : 'New Question'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Question</Label><Input value={q.question} onChange={e => setQ({ ...q, question: e.target.value })} data-testid="question-text" /></div>
          {q.options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="radio" checked={q.correct_index === i} onChange={() => setQ({ ...q, correct_index: i })} data-testid={`question-correct-${i}`} />
              <Input value={o} placeholder={`Option ${i + 1}`} onChange={e => { const opts = [...q.options]; opts[i] = e.target.value; setQ({ ...q, options: opts }); }} data-testid={`question-option-${i}`} />
            </div>
          ))}
          <p className="text-[11px] text-brand-text-secondary">Select the radio button next to the correct answer.</p>
          <Button onClick={save} disabled={saving} className="w-full bg-brand-primary text-white" data-testid="question-save-btn">{saving ? <Loader2 size={15} className="animate-spin" /> : 'Save'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ExamQuestionsTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { api.get('/sales/exam/questions').then(r => setRows(r.data)).catch(err => toast.error(formatError(err.response?.data?.detail))); }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try { await api.delete(`/sales/exam/questions/${id}`); toast.success('Deleted'); load(); } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  return (
    <Card className="border border-brand-border bg-white">
      <div className="p-3 flex items-center justify-between border-b border-brand-border">
        <p className="text-xs text-brand-text-secondary" data-testid="question-count">{rows.length} questions · pass mark 70% · retry after 24 hours</p>
        <Button onClick={() => setEditing({})} className="bg-brand-primary text-white h-9 text-sm" data-testid="add-question-btn"><Plus size={15} className="mr-1" /> Add Question</Button>
      </div>
      <ol className="divide-y divide-brand-border/60">
        {rows.map((q, i) => (
          <li key={q.id} className="p-4 flex gap-3" data-testid={`question-row-${i + 1}`}>
            <span className="font-mono text-xs text-brand-text-secondary w-6 pt-0.5">{i + 1}.</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-brand-text-primary">{q.question}</p>
              <p className="text-xs text-emerald-700 mt-1">Answer: {q.options[q.correct_index]}</p>
            </div>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(q)} data-testid={`edit-question-${i + 1}`}><Pencil size={14} /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => remove(q.id)} data-testid={`delete-question-${i + 1}`}><Trash2 size={14} /></Button>
          </li>
        ))}
      </ol>
      <QuestionDialog value={editing} onClose={() => setEditing(null)} onSaved={load} />
    </Card>
  );
}
